import React, { useContext, useState } from "react";
import Form from "../../../ui/Form/Form";
import AddRange from "../../../ui/range/AddRange";
import TabPanel from "../../../ui/tabs/TabPanel";
import UpdateExistingRange from "../../../ui/range/UpdateExistingRange";
import { errorAlert, makeApiRequest, successAlert } from "../../../util";
import AuthContext from "../../../store/authContext";
import InfoAlert from "../../../ui/InfoAlert";
import {
  CREATE_XSSI_CONNECTION_TRANSLATIONS,
  EDIT_XSSI_CONNECTION_TRANSLATIONS,
} from "../../../translations/util";

interface IProps {
  ranges: Array<{
    "UNIT-MIN-ID": number;
    "UNIT-MAX-ID": number;
    ID: number;
    CreationDate: string;
    ModificationDate: string;
  }>;
  xssiRef: string;
  org: string;
  refetchConnection: () => Promise<void>;
  hasEditPermission: boolean;
  hasDeletePermission: boolean;
}

const UpdateUnitRanges = ({
  ranges,
  xssiRef,
  org,
  refetchConnection,
  hasEditPermission,
  hasDeletePermission,
}: IProps) => {
  const { token } = useContext(AuthContext);
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const addToRange = async (min: number, max: number) => {
    // The range type (unit, iwf-group or radio-group).
    setAddLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/xssi-connection-ranges/${xssiRef}/range/unit`,
        method: "POST",
        body: JSON.stringify({
          "MIN-ID": +min,
          "MAX-ID": +max,
        }),
        token,
        org,
        emptyResponse: true,
      });
      successAlert(EDIT_XSSI_CONNECTION_TRANSLATIONS.range.successAlertMessage);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      refetchConnection();
      setAddLoading(false);
    }
  };

  const deleteRange = (idx: number) => async () => {
    setDeleteLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/xssi-connection-ranges/${xssiRef}/range/unit/${idx}`,
        method: "DELETE",
        token,
        org,
        emptyResponse: true,
      });
      successAlert(
        EDIT_XSSI_CONNECTION_TRANSLATIONS.range.deleteSuccessAlertMessage
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      refetchConnection();
      setDeleteLoading(false);
    }
  };

  const updateRange = async (min: number, max: number, id: number) => {
    setEditLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/xssi-connection-ranges/${xssiRef}/range/unit/${id}`,
        method: "PUT",
        body: JSON.stringify({
          "MIN-ID": +min,
          "MAX-ID": +max,
        }),
        token,
        org,
        emptyResponse: true,
      });
      successAlert(
        EDIT_XSSI_CONNECTION_TRANSLATIONS.range.editSuccessAlertMessage
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      refetchConnection();
      setEditLoading(false);
    }
  };

  return (
    <div className="data-tabcontent">
      <TabPanel>
        <div className="data-form">
          <Form>
            {hasEditPermission ? (
              <AddRange
                loading={addLoading}
                addRangeTitle={
                  CREATE_XSSI_CONNECTION_TRANSLATIONS.ranges
                    .createNewUnitRangeText
                }
                labelPrefix={
                  EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.unitLabelPrefix
                }
                addToRange={addToRange}
              />
            ) : null}
            <div className="data-card">
              <h3
                data-cy={`${EDIT_XSSI_CONNECTION_TRANSLATIONS.range.unitRanges.replaceAll(
                  " ",
                  ""
                )}`}
              >
                {" "}
                {EDIT_XSSI_CONNECTION_TRANSLATIONS.range.unitRanges}{" "}
              </h3>
              {ranges.length < 1 ? (
                <InfoAlert
                  heading={
                    EDIT_XSSI_CONNECTION_TRANSLATIONS.range.unitRangeInfoAlert
                      .heading
                  }
                  subText={
                    EDIT_XSSI_CONNECTION_TRANSLATIONS.range.unitRangeInfoAlert
                      .subText
                  }
                />
              ) : null}
              {ranges.map((range) => {
                // eslint-disable-next-line react/no-array-index-key
                return (
                  <UpdateExistingRange
                    editloading={editLoading}
                    deleteLoading={deleteLoading}
                    key={range.ID}
                    labelPrefix={
                      EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels
                        .unitLabelPrefix
                    }
                    range={{
                      id: range.ID,
                      creationDate: range.CreationDate,
                      modificationDate: range.ModificationDate,
                      min: range["UNIT-MIN-ID"],
                      max: range["UNIT-MAX-ID"],
                    }}
                    deleteRange={deleteRange(range.ID)}
                    updateRange={updateRange}
                    hasEditPermission={hasEditPermission}
                    hasDeletePermission={hasDeletePermission}
                  />
                );
              })}
            </div>
          </Form>
        </div>
      </TabPanel>
    </div>
  );
};

export default UpdateUnitRanges;
